import { AnimatedDialog, AnimatedDialogContent, AnimatedDialogDescription, AnimatedDialogHeader, AnimatedDialogTitle } from "components/ui/animated-dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { AlertCircle,Loader2 } from "lucide-react";
import React, { useState } from "react";
import config from "src/common/config";

interface AwsMarketplaceRegistrationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
}

// REST API registration function
const registerWithAPI = async (data: {
  customerId: string;
  organizationName: string;
  firstName: string;
  lastName: string;
  email: string;
}) => {
  const backendUrl = config.get("APP_WWW_URL");
  const response = await fetch(`${backendUrl}/aws_marketplace/register`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      customer_id: data.customerId,
      organization_name: data.organizationName,
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export default function AwsMarketplaceRegistrationDialog({
  isOpen,
  onClose,
  customerId,
}: AwsMarketplaceRegistrationDialogProps) {
  const [formData, setFormData] = useState({
    organizationName: "",
    firstName: "",
    lastName: "",
    email: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [isRegistering, setIsRegistering] = useState(false);

  const handleInputChange = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.organizationName.trim() || !formData.firstName.trim() || 
        !formData.lastName.trim() || !formData.email.trim()) {
      setError("Please fill in all fields");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsRegistering(true);
    setError(null);

    try {
      const result = await registerWithAPI({
        customerId: customerId,
        organizationName: formData.organizationName.trim(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
      });

      if (result.error) {
        setError(result.error);
      } else if (result.success) {
        // Success - redirect to registration complete page
        window.location.href = "/?registration_complete=true";
      } else {
        setError("Unexpected response from server");
      }
    } catch (error) {
      console.error("Registration error:", error);
      setError(`Registration failed: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsRegistering(false);
    }
  };

  const handleClose = () => {
    if (!isRegistering) {
      setError(null);
      setFormData({
        organizationName: "",
        firstName: "",
        lastName: "",
        email: "",
      });
      onClose();
    }
  };

  return (
    <AnimatedDialog open={isOpen} onOpenChange={handleClose}>
      <AnimatedDialogContent className="sm:max-w-md bg-steel border-steel/50 text-white">
        <AnimatedDialogHeader>
          <AnimatedDialogTitle className="text-transparent bg-clip-text bg-gradient-to-r from-cyan to-[#56A64B]">
            Complete Your Registration
          </AnimatedDialogTitle>
          <AnimatedDialogDescription className="text-slate-300">
            Please provide your details to complete your AWS Marketplace registration.
          </AnimatedDialogDescription>
        </AnimatedDialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-red-300 text-sm">
                {error}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="organizationName" className="text-slate-300">
              Organization Name *
            </Label>
            <Input
              id="organizationName"
              type="text"
              value={formData.organizationName}
              onChange={handleInputChange("organizationName")}
              placeholder="Your company name"
              disabled={isRegistering}
              className="bg-dark-steel border-steel-border text-white placeholder:text-slate-400"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-slate-300">
                First Name *
              </Label>
              <Input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleInputChange("firstName")}
                placeholder="John"
                disabled={isRegistering}
                className="bg-dark-steel border-steel-border text-white placeholder:text-slate-400"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-slate-300">
                Last Name *
              </Label>
              <Input
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleInputChange("lastName")}
                placeholder="Doe"
                disabled={isRegistering}
                className="bg-dark-steel border-steel-border text-white placeholder:text-slate-400"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-300">
              Work Email *
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange("email")}
              placeholder="john.doe@company.com"
              disabled={isRegistering}
              className="bg-dark-steel border-steel-border text-white placeholder:text-slate-400"
              required
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isRegistering}
              className="flex-1 bg-slate-600 hover:bg-slate-700 text-white border-slate-500"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isRegistering}
              className="flex-1 bg-gradient-to-r from-cyan to-[#56A64B] hover:from-[#56A64B] hover:to-cyan text-white font-medium"
            >
              {isRegistering ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Registering...
                </span>
              ) : (
                "Complete Registration"
              )}
            </Button>
          </div>
        </form>
      </AnimatedDialogContent>
    </AnimatedDialog>
  );
}